/**
 * Membership plan-credits API wrapper (v2 — Project Independence, DNKADM-0056 U1).
 *
 * Each membership plan carries a set of **per-bucket credit deposits** — the credits
 * granted on purchase/renewal, keyed by `(planId, creditBucketId)` (handoff §5.9).
 * This is the v2 re-point of the v1 tier×bucket "tier-credits matrix": the v1 tier
 * STRING is replaced by a **plan id**, and the v1 bucket STRING by a **credit-bucket
 * id** (0052). There is no live-axes/orphan grid here — a plan owns a flat list of
 * `(bucket, amount)` rows, edited inline per plan (mirrors the 0055 per-staff price
 * editor, not the v1 matrix).
 *
 * Bearer-only `/v2/admin/**` surface — `request()` routes it (DNKADM-0049
 * `isV2Surface`): bearer token, NO CSRF, NO cookies, the bounded 401→refresh→retry;
 * the mutating-audit `X-Session-Mode` stamp fires on the writes.
 *
 * **Endpoints (handoff §5.9, §3.1 — standard REST nested under the plan):**
 *  - `GET    /v2/admin/membership-plans/{id}/credits` → {@link PlanCredit}`[]`.
 *  - `PUT    /v2/admin/membership-plans/{id}/credits/{creditBucketId}` ← `{ amount }`
 *           (recurring) or `{ amount, recurrence:'add_on', reEligibilityLapseMonths,
 *           expiryDays }` (add-on, DNKADM-0108) — upsert by bucket; id-in-path uniqueness.
 *  - `DELETE /v2/admin/membership-plans/{id}/credits/{creditBucketId}` (remove a deposit).
 *
 * Reads are parsed DEFENSIVELY (mirrors `services.ts` staff-prices): a missing/
 * wrong-typed field degrades (a row with no usable bucket id is dropped), never a throw.
 */
import { request } from './client';

/** v2 admin membership-plans base path (bearer surface). */
const PATH = '/v2/admin/membership-plans';

/**
 * A plan-credit grant's recurrence (DNKADM-0108 / DNKAPI-0205/0206):
 *  - `recurring` — granted every billing cycle (purchase + each renewal). The default.
 *  - `add_on` — granted once on a qualifying first purchase (not renewals), with its
 *    own re-eligibility window + expiry.
 */
type CreditRecurrence = 'recurring' | 'add_on';

/**
 * One `v2_membership_plan_credit` row — a per-bucket deposit on a plan. Keyed by
 * `creditBucketId` (the 0052 first-class bucket id, NOT a string); `amount` is the
 * credits granted per purchase/renewal (DECIMAL(8,2), non-negative).
 *
 * DNKADM-0108: a grant now carries a `recurrence` — `recurring` (every cycle, the
 * default) or `add_on` (first purchase only). For an add-on,
 * `reEligibilityLapseMonths` (`0` = once ever; `N` = re-eligible after an N-month
 * lapse) and `expiryDays` (`0` = never expires; `N` = the lot expires N days after
 * purchase) apply. For a recurring grant both knobs are `0` (unused).
 */
export interface PlanCredit {
  creditBucketId: string;
  amount: number;
  recurrence: CreditRecurrence;
  reEligibilityLapseMonths: number;
  expiryDays: number;
}

// --- Defensive parsing helpers (mirrors services.ts) ---------------------------

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** A trimmed non-empty string, or null. */
function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * A stable string id from a NUMBER or a string (DNKADM-0064): the `creditBucketId` fk
 * arrives as a number on the wire and is normalized to the `string` the UI keys off —
 * a finite number → `String(n)`, a non-empty string → trimmed, else null.
 */
function asId(value: unknown): string | null {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  return asText(value);
}

/** A finite number (accepts numeric strings, e.g. a BigDecimal serialized as text), or null. */
function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/**
 * Parse one raw plan-credit, or null when it has no usable `creditBucketId` (a row
 * that can't be keyed to a bucket can't be rendered or edited — drop it). A missing/
 * negative `amount` degrades to 0 (the server is the authority; the editor enforces
 * the real non-negative amount on write).
 */
function parseCredit(raw: unknown): PlanCredit | null {
  if (!isObject(raw)) return null;
  // Nested credit-bucket fk id — numeric on the wire (DNKADM-0064).
  const creditBucketId = asId(raw['creditBucketId']);
  if (creditBucketId === null) return null;
  const amount = asNumber(raw['amount']);
  // DNKADM-0108: recurrence (unknown/absent → 'recurring') + the two add-on knobs,
  // each floored to a non-negative integer (a recurring grant carries 0/0).
  const recurrence: CreditRecurrence =
    asText(raw['recurrence'])?.toLowerCase() === 'add_on' ? 'add_on' : 'recurring';
  const reEligibilityLapseMonths = Math.max(
    0,
    Math.trunc(asNumber(raw['reEligibilityLapseMonths']) ?? 0),
  );
  const expiryDays = Math.max(0, Math.trunc(asNumber(raw['expiryDays']) ?? 0));
  return {
    creditBucketId,
    amount: amount !== null && amount >= 0 ? amount : 0,
    recurrence,
    reEligibilityLapseMonths,
    expiryDays,
  };
}

/** Tolerantly extract the credits array (bare array or `{ credits }`/`{ content }`). */
function extractCredits(body: unknown): unknown[] {
  if (Array.isArray(body)) return body;
  if (isObject(body)) {
    if (Array.isArray(body['credits'])) return body['credits'];
    if (Array.isArray(body['content'])) return body['content'];
  }
  return [];
}

function creditsPath(planId: string): string {
  return `${PATH}/${encodeURIComponent(planId)}/credits`;
}

function creditPath(planId: string, creditBucketId: string): string {
  return `${creditsPath(planId)}/${encodeURIComponent(creditBucketId)}`;
}

// --- Endpoints -----------------------------------------------------------------

/**
 * `GET /v2/admin/membership-plans/{id}/credits` → the plan's per-bucket deposits,
 * defensively parsed (a bad entry dropped). Throws a typed `ApiError`. Refetch after
 * every write.
 */
export async function listPlanCredits(planId: string): Promise<PlanCredit[]> {
  const body = await request<unknown>('GET', creditsPath(planId));
  return extractCredits(body)
    .map(parseCredit)
    .filter((c): c is PlanCredit => c !== null);
}

/**
 * Write input for {@link setPlanCredit} (DNKADM-0108). `amount` is required (`> 0`).
 * `recurrence` defaults to `recurring`; the two knobs apply to an `add_on` only.
 */
export interface PlanCreditWrite {
  amount: number;
  recurrence?: CreditRecurrence;
  reEligibilityLapseMonths?: number;
  expiryDays?: number;
}

/**
 * `PUT /v2/admin/membership-plans/{id}/credits/{creditBucketId}` → upsert one
 * `(bucket, amount)` grant (the id-in-path upsert enforces the unique `(plan, bucket)`
 * rule). Throws a typed `ApiError` (`400` VALIDATION, `404` NOT_FOUND, upstream
 * forwarded). Refetch after this resolves.
 *
 * Body rule (DNKADM-0108 / DNKAPI-0205/0206):
 *  - `add_on` ⇒ `{ amount, recurrence:'add_on', reEligibilityLapseMonths, expiryDays }`
 *    (the two knobs floored to non-negative integers);
 *  - `recurring` ⇒ `{ amount }` ONLY — the three optional fields are OMITTED so the
 *    write stays backward-compatible with the pre-0108 recurring-only contract.
 */
export async function setPlanCredit(
  planId: string,
  creditBucketId: string,
  input: PlanCreditWrite,
): Promise<void> {
  const recurrence = input.recurrence ?? 'recurring';
  const json: Record<string, unknown> =
    recurrence === 'add_on'
      ? {
          amount: input.amount,
          recurrence: 'add_on',
          reEligibilityLapseMonths: Math.max(0, Math.trunc(input.reEligibilityLapseMonths ?? 0)),
          expiryDays: Math.max(0, Math.trunc(input.expiryDays ?? 0)),
        }
      : { amount: input.amount };
  await request<unknown>('PUT', creditPath(planId, creditBucketId), { json });
}

/**
 * `DELETE /v2/admin/membership-plans/{id}/credits/{creditBucketId}` → remove a
 * deposit (the plan no longer grants that bucket). Throws a typed `ApiError` (`404`
 * NOT_FOUND, upstream forwarded). Refetch after this resolves.
 */
export async function deletePlanCredit(planId: string, creditBucketId: string): Promise<void> {
  await request<unknown>('DELETE', creditPath(planId, creditBucketId));
}
